import React, { useRef, useEffect } from 'react';
import {
  useCircuitStore,
  MAX_STEPS,
  PRESET_CIRCUITS,
} from '../../store/circuitStore';
import {
  CircuitCell,
  CnotControlCell,
  CnotTargetCell,
  CircuitResult,
} from '../../engine/circuitEngine';

// ─── Gate palette definitions ─────────────────────────────────────────────────

interface GateDef {
  id: string;
  label: string;
  color: string;
  bg: string;
  description: string;
}

const GATE_DEFS: GateDef[] = [
  { id: 'H', label: 'H', color: '#6366f1', bg: '#ede9fe', description: 'Hadamard – creates superposition' },
  { id: 'X', label: 'X', color: '#ef4444', bg: '#fee2e2', description: 'Pauli-X – bit flip (quantum NOT)' },
  { id: 'Y', label: 'Y', color: '#f97316', bg: '#ffedd5', description: 'Pauli-Y – bit+phase flip' },
  { id: 'Z', label: 'Z', color: '#0ea5e9', bg: '#e0f2fe', description: 'Pauli-Z – phase flip' },
  { id: 'S', label: 'S', color: '#10b981', bg: '#d1fae5', description: 'S gate – 90° phase rotation' },
  { id: 'T', label: 'T', color: '#8b5cf6', bg: '#ede9fe', description: 'T gate – 45° phase rotation' },
  { id: 'CNOT', label: '●─X', color: '#f59e0b', bg: '#fef3c7', description: 'CNOT – click control then target qubit' },
  { id: 'MEASURE', label: 'M', color: '#64748b', bg: '#f1f5f9', description: 'Measure – collapse qubit to 0 or 1' },
];

// ─── Main CircuitBuilder ──────────────────────────────────────────────────────

export default function CircuitBuilder() {
  const {
    numQubits, setNumQubits,
    initialStates, setInitialState,
    grid,
    selectedGate, setSelectedGate,
    cnotPending,
    result, currentStep,
    activePreset,
    clickCell, clearCell,
    run, stepForward, reset, clearGrid,
    loadPreset,
  } = useCircuitStore();

  const displayResult = result
    ? { ...result, probabilities: result.stepStates[currentStep]?.state.map(c => c[0]*c[0]+c[1]*c[1]) ?? result.probabilities }
    : null;

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden', fontFamily: 'system-ui, sans-serif' }}>

      {/* ── Left: Controls & Gate Palette ── */}
      <div style={{
        width: 220,
        background: '#f8fafc',
        borderRight: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        flexShrink: 0,
      }}>
        {/* Preset picker */}
        <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Preset Circuits
          </div>
          <select
            value={activePreset?.id ?? ''}
            onChange={e => e.target.value && loadPreset(e.target.value)}
            style={{ width: '100%', padding: '5px 8px', fontSize: 11, borderRadius: 6, border: '1.5px solid #e2e8f0', background: 'white', color: '#374151' }}
          >
            <option value="">Choose a preset…</option>
            {PRESET_CIRCUITS.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {activePreset && (
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 6, lineHeight: 1.4 }}>
              {activePreset.description}
            </div>
          )}
        </div>

        {/* Qubit count */}
        <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Qubits
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {([1, 2, 3] as const).map(n => (
              <button key={n} onClick={() => setNumQubits(n)} style={{
                flex: 1, padding: '5px 0', fontSize: 12, fontWeight: 700,
                border: `1.5px solid ${numQubits === n ? '#6366f1' : '#e2e8f0'}`,
                borderRadius: 6, cursor: 'pointer',
                background: numQubits === n ? '#ede9fe' : 'white',
                color: numQubits === n ? '#6366f1' : '#64748b',
              }}>
                {n}
              </button>
            ))}
          </div>
          {/* Initial states */}
          <div style={{ marginTop: 8 }}>
            {Array.from({ length: numQubits }, (_, q) => (
              <div key={q} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: '#475569', width: 24 }}>q{q}</span>
                <button
                  onClick={() => setInitialState(q, initialStates[q] === '0' ? '1' : '0')}
                  style={{
                    padding: '2px 8px', fontSize: 11, fontWeight: 700,
                    border: '1.5px solid #e2e8f0', borderRadius: 6, cursor: 'pointer',
                    background: initialStates[q] === '1' ? '#fef3c7' : '#f1f5f9',
                    color: initialStates[q] === '1' ? '#b45309' : '#475569',
                  }}
                >
                  |{initialStates[q]}⟩
                </button>
                <span style={{ fontSize: 9, color: '#94a3b8' }}>click to flip</span>
              </div>
            ))}
          </div>
        </div>

        {/* Gate palette */}
        <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Gate Palette
          </div>
          <div style={{ fontSize: 9, color: '#94a3b8', marginBottom: 8, lineHeight: 1.4 }}>
            {cnotPending
              ? '⚡ CNOT: now click the target qubit in the same column'
              : selectedGate
              ? `Selected: ${selectedGate} — click a cell to place`
              : 'Select a gate, then click a cell in the circuit'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {GATE_DEFS.map(def => (
              <button
                key={def.id}
                title={def.description}
                onClick={() => setSelectedGate(selectedGate === def.id ? null : def.id)}
                style={{
                  width: 44, height: 36, fontSize: def.label.length > 2 ? 9 : 13,
                  fontWeight: 700, fontFamily: 'monospace',
                  border: `2px solid ${selectedGate === def.id ? def.color : '#e2e8f0'}`,
                  borderRadius: 7, cursor: 'pointer',
                  background: selectedGate === def.id ? def.bg : 'white',
                  color: selectedGate === def.id ? def.color : '#475569',
                  transition: 'all 0.1s',
                  boxShadow: selectedGate === def.id ? `0 0 0 2px ${def.color}33` : 'none',
                }}
              >
                {def.label}
              </button>
            ))}
          </div>
          {selectedGate && (
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 6, lineHeight: 1.4, fontStyle: 'italic' }}>
              {GATE_DEFS.find(g => g.id === selectedGate)?.description}
            </div>
          )}
        </div>

        {/* Run controls */}
        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <RunButton onClick={run} color="#6366f1">▶ Run Circuit</RunButton>
          <RunButton onClick={stepForward} color="#0ea5e9" outline>⏭ Step Through</RunButton>
          <div style={{ display: 'flex', gap: 4 }}>
            <RunButton onClick={reset} color="#64748b" outline small>↺ Reset</RunButton>
            <RunButton onClick={clearGrid} color="#ef4444" outline small>✕ Clear</RunButton>
          </div>
        </div>

        {/* Explanation */}
        {activePreset && result && (
          <div style={{
            margin: '0 12px 12px',
            background: 'linear-gradient(135deg, #ede9fe, #dbeafe)',
            border: '1px solid #c4b5fd',
            borderRadius: 8,
            padding: '8px 10px',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#5b21b6', marginBottom: 3 }}>💡 What happened?</div>
            <div style={{ fontSize: 10, color: '#3b0764', lineHeight: 1.5 }}>{activePreset.explanation}</div>
          </div>
        )}
      </div>

      {/* ── Center: Circuit Grid ── */}
      <div style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', background: '#f0f4ff', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
        <CircuitGrid
          numQubits={numQubits}
          initialStates={initialStates}
          grid={grid}
          selectedGate={selectedGate}
          cnotPending={cnotPending}
          onClickCell={clickCell}
          onRightClickCell={clearCell}
        />
      </div>

      {/* ── Right: State Vector ── */}
      <div style={{
        width: 240,
        background: 'white',
        borderLeft: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        flexShrink: 0,
      }}>
        <StateVectorPanel result={result} currentStep={currentStep} numQubits={numQubits} />
      </div>
    </div>
  );
}

// ─── Circuit Grid ─────────────────────────────────────────────────────────────

function CircuitGrid({
  numQubits, initialStates, grid, selectedGate, cnotPending, onClickCell, onRightClickCell,
}: {
  numQubits: number;
  initialStates: ('0' | '1')[];
  grid: (CircuitCell | null)[][];
  selectedGate: string | null;
  cnotPending: { step: number; controlQubit: number } | null;
  onClickCell: (step: number, qubit: number) => void;
  onRightClickCell: (step: number, qubit: number) => void;
}) {
  const CELL_W = 64;
  const CELL_H = 60;
  const QUBIT_LABEL_W = 60;
  const WIRE_COLOR = '#94a3b8';

  return (
    <div style={{ padding: '32px 24px', minWidth: 'max-content' }}>
      {Array.from({ length: numQubits }, (_, q) => {
        const initLabel = `q${q} |${initialStates[q]}⟩`;
        return (
          <div key={q} style={{ display: 'flex', alignItems: 'center', height: CELL_H, position: 'relative' }}>
            {/* Qubit label */}
            <div style={{
              width: QUBIT_LABEL_W,
              textAlign: 'right',
              paddingRight: 12,
              fontFamily: 'monospace',
              fontSize: 13,
              fontWeight: 700,
              color: '#475569',
              flexShrink: 0,
            }}>
              {initLabel}
            </div>

            {/* Wire + cells */}
            {Array.from({ length: MAX_STEPS }, (_, step) => {
              const cell = grid[step][q];
              const isPendingControl = cnotPending?.step === step && cnotPending?.controlQubit === q;

              return (
                <div key={step} style={{ position: 'relative', width: CELL_W, height: CELL_H, flexShrink: 0 }}>
                  {/* Horizontal wire */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    right: 0,
                    height: 2,
                    background: WIRE_COLOR,
                    zIndex: 0,
                  }} />

                  {/* Vertical CNOT line */}
                  {cell?.gate === 'CNOT' && (
                    <CnotLine
                      controlQubit={q}
                      targetQubit={(cell as CnotControlCell).targetQubit}
                      cellH={CELL_H}
                    />
                  )}

                  {/* Gate cell */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 2,
                      cursor: selectedGate ? 'pointer' : 'default',
                    }}
                    onClick={() => onClickCell(step, q)}
                    onContextMenu={e => { e.preventDefault(); onRightClickCell(step, q); }}
                  >
                    <GateCellView
                      cell={cell}
                      isPendingControl={isPendingControl}
                      selectedGate={selectedGate}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 12, textAlign: 'center' }}>
        Left-click to place · Right-click to remove · CNOT: click control then target
      </div>
    </div>
  );
}

function CnotLine({ controlQubit, targetQubit, cellH }: { controlQubit: number; targetQubit: number; cellH: number }) {
  const dir = targetQubit > controlQubit ? 1 : -1;
  const dist = Math.abs(targetQubit - controlQubit);
  const lineH = dist * cellH;

  return (
    <div style={{
      position: 'absolute',
      left: '50%',
      top: dir === 1 ? '50%' : `calc(50% - ${lineH}px)`,
      width: 2,
      height: lineH,
      background: '#f59e0b',
      zIndex: 1,
      transform: 'translateX(-50%)',
    }} />
  );
}

function GateCellView({
  cell, isPendingControl, selectedGate,
}: {
  cell: CircuitCell | null;
  isPendingControl: boolean;
  selectedGate: string | null;
}) {
  const SIZE = 38;

  if (isPendingControl) {
    return (
      <div style={{
        width: SIZE, height: SIZE, borderRadius: '50%',
        background: '#fef3c7', border: '2.5px dashed #f59e0b',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 20, color: '#f59e0b',
      }}>●</div>
    );
  }

  if (!cell) {
    return (
      <div style={{
        width: SIZE, height: SIZE,
        border: `1.5px dashed ${selectedGate ? '#c7d2fe' : '#e2e8f0'}`,
        borderRadius: 8,
        background: selectedGate ? '#f5f3ff' : 'transparent',
        transition: 'all 0.1s',
      }} />
    );
  }

  if (cell.gate === 'CNOT') {
    return (
      <div style={{
        width: 18, height: 18, borderRadius: '50%',
        background: '#f59e0b', border: '2px solid #d97706',
      }} />
    );
  }

  if (cell.gate === 'CNOT_TARGET') {
    return (
      <div style={{
        width: SIZE, height: SIZE, borderRadius: '50%',
        border: '2.5px solid #f59e0b',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#d97706', fontSize: 22, fontWeight: 700, fontFamily: 'monospace',
      }}>⊕</div>
    );
  }

  if (cell.gate === 'MEASURE') {
    return (
      <div style={{
        width: SIZE, height: SIZE, borderRadius: 8,
        background: '#f1f5f9', border: '2px solid #64748b',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        color: '#475569',
      }}>
        <div style={{ fontSize: 14 }}>⊻</div>
        <div style={{ fontSize: 7, fontWeight: 700 }}>MEAS</div>
      </div>
    );
  }

  const def = GATE_DEFS.find(g => g.id === cell.gate);
  return (
    <div style={{
      width: SIZE, height: SIZE, borderRadius: 8,
      background: def?.bg ?? '#f1f5f9',
      border: `2px solid ${def?.color ?? '#64748b'}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 14, fontWeight: 800, fontFamily: 'monospace',
      color: def?.color ?? '#475569',
      boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
    }}>
      {cell.gate}
    </div>
  );
}

// ─── State Vector Panel ───────────────────────────────────────────────────────

function StateVectorPanel({
  result,
  currentStep,
  numQubits,
}: {
  result: CircuitResult | null;
  currentStep: number;
  numQubits: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const PANEL_W = 220;

  // Extract current step's probabilities
  const stepState = result?.stepStates[currentStep];
  const probs = stepState?.state.map(c => c[0] * c[0] + c[1] * c[1]) ?? null;
  const labels = result?.basisLabels ?? [];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const W = PANEL_W;
    const H = canvas.height;

    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, W, H);

    if (!probs || probs.length === 0) {
      ctx.fillStyle = '#94a3b8';
      ctx.font = '11px system-ui';
      ctx.textAlign = 'center';
      ctx.fillText('Run the circuit to see', W / 2, H / 2 - 8);
      ctx.fillText('the quantum state', W / 2, H / 2 + 8);
      return;
    }

    const pad = { top: 30, bottom: 8, left: 10, right: 10 };
    const n = probs.length;
    const barAreaH = H - pad.top - pad.bottom;
    const barH = Math.max(4, Math.floor((barAreaH - (n - 1) * 4) / n));
    const maxBarW = W - pad.left - pad.right - 52; // room for label + %

    // Title
    ctx.font = 'bold 11px system-ui';
    ctx.fillStyle = '#475569';
    ctx.textAlign = 'left';
    ctx.fillText(stepState?.label ?? 'State', 8, 16);

    // Draw bars
    probs.forEach((p, i) => {
      const y = pad.top + i * (barH + 4);
      const bw = maxBarW * p;

      // Background track
      ctx.fillStyle = '#e2e8f0';
      ctx.beginPath();
      ctx.roundRect(pad.left + 42, y, maxBarW, barH, 3);
      ctx.fill();

      // Bar
      const hue = p > 0.01 ? 240 : 0;
      ctx.fillStyle = p > 0.01 ? `hsl(${248 - i * 15},70%,58%)` : '#e2e8f0';
      if (bw > 2) {
        ctx.beginPath();
        ctx.roundRect(pad.left + 42, y, bw, barH, 3);
        ctx.fill();
      }

      // Label
      ctx.font = `bold ${barH >= 16 ? 11 : 9}px monospace`;
      ctx.fillStyle = '#374151';
      ctx.textAlign = 'right';
      ctx.fillText(labels[i] ?? `|${i}⟩`, pad.left + 38, y + barH * 0.72);

      // Percentage
      ctx.font = `${barH >= 16 ? 10 : 8}px system-ui`;
      ctx.fillStyle = p > 0.01 ? '#4338ca' : '#94a3b8';
      ctx.textAlign = 'left';
      ctx.fillText(`${Math.round(p * 100)}%`, pad.left + 42 + maxBarW + 4, y + barH * 0.72);
    });
  }, [probs, labels, stepState]);

  const canvasH = Math.max(160, Math.min(400, 30 + (1 << numQubits) * 28));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '12px 14px 6px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          State Vector
        </div>
        <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>
          Probability of each basis state
        </div>
      </div>

      <div style={{ padding: 10, flex: 1, overflowY: 'auto' }}>
        <canvas
          ref={canvasRef}
          width={PANEL_W}
          height={canvasH}
          style={{ borderRadius: 8, display: 'block', maxWidth: '100%' }}
        />

        {/* Step indicator */}
        {result && (
          <div style={{ marginTop: 10 }}>
            <StepScrubber result={result} />
          </div>
        )}

        {/* Measurement results */}
        {result && Object.keys(result.measureResults).length > 0 && (
          <div style={{ marginTop: 10, background: '#f8fafc', borderRadius: 8, padding: '8px 10px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 5 }}>
              Measurement Outcomes
            </div>
            {Object.entries(result.measureResults).map(([q, v]) => (
              <div key={q} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <span style={{ fontSize: 11, color: '#475569', fontFamily: 'monospace', width: 24 }}>q{q}</span>
                <span style={{
                  padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                  background: v === 0 ? '#ede9fe' : '#fef3c7',
                  color: v === 0 ? '#6366f1' : '#d97706',
                }}>|{v}⟩</span>
              </div>
            ))}
          </div>
        )}

        {/* Bloch hint for 1-qubit */}
        {numQubits === 1 && result && (
          <BlochSphere result={result} currentStep={currentStep} />
        )}
      </div>
    </div>
  );
}

function StepScrubber({ result }: { result: CircuitResult }) {
  const { currentStep, stepForward } = useCircuitStore();
  const total = result.stepStates.length - 1;

  return (
    <div style={{ fontSize: 10, color: '#64748b' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <span>Step {currentStep} / {total}</span>
        <button
          onClick={stepForward}
          style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
            border: '1.5px solid #6366f1', background: '#ede9fe', color: '#6366f1', fontWeight: 600,
          }}
        >
          Next step →
        </button>
      </div>
      <div style={{ height: 4, background: '#e2e8f0', borderRadius: 2 }}>
        <div style={{
          height: '100%', borderRadius: 2, background: '#6366f1',
          width: `${total > 0 ? (currentStep / total) * 100 : 0}%`,
          transition: 'width 0.2s',
        }} />
      </div>
      <div style={{ marginTop: 3, fontSize: 9, color: '#94a3b8', textAlign: 'center' }}>
        {result.stepStates[currentStep]?.label}
      </div>
    </div>
  );
}

/** Simplified 2D Bloch sphere projection for 1-qubit experiments */
function BlochSphere({ result, currentStep }: { result: CircuitResult; currentStep: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const W = 200, H = 200, R = 75;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const cx = W / 2, cy = H / 2;

    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, W, H);

    // Sphere outline
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, Math.PI * 2);
    ctx.stroke();

    // Axes
    ctx.strokeStyle = '#e2e8f0';
    ctx.setLineDash([4, 4]);
    ctx.beginPath(); ctx.moveTo(cx, cy - R - 10); ctx.lineTo(cx, cy + R + 10); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx - R - 10, cy); ctx.lineTo(cx + R + 10, cy); ctx.stroke();
    ctx.setLineDash([]);

    // Labels
    ctx.font = 'bold 11px monospace';
    ctx.fillStyle = '#475569';
    ctx.textAlign = 'center';
    ctx.fillText('|0⟩', cx, cy - R - 14);
    ctx.fillText('|1⟩', cx, cy + R + 20);
    ctx.fillText('+', cx + R + 16, cy + 4);
    ctx.fillText('−', cx - R - 14, cy + 4);

    // Current state arrow
    const stepState = result.stepStates[currentStep];
    if (!stepState || stepState.state.length < 2) return;
    const [ar, ai] = stepState.state[0]; // |0⟩ amplitude
    const [br, bi] = stepState.state[1]; // |1⟩ amplitude
    const p0 = ar * ar + ai * ai;
    const p1 = br * br + bi * bi;

    // θ on Bloch sphere: p0 = cos²(θ/2), so θ = 2*acos(sqrt(p0))
    const theta = 2 * Math.acos(Math.min(1, Math.sqrt(p0)));
    // φ (azimuthal) from relative phase: φ = arg(β) - arg(α)
    const phi = Math.atan2(bi, br) - Math.atan2(ai, ar);

    // Map to 2D (projection onto XZ plane, with X = sin(θ)cos(φ), Z = cos(θ))
    const bx = Math.sin(theta) * Math.cos(phi);
    const bz = Math.cos(theta);

    const ax = cx + R * bx;
    const ay = cy - R * bz; // Y axis inverted on canvas

    // Arrow
    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(ax, ay);
    ctx.stroke();

    // Arrowhead
    const angle = Math.atan2(ay - cy, ax - cx);
    ctx.fillStyle = '#6366f1';
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(ax - 8 * Math.cos(angle - 0.4), ay - 8 * Math.sin(angle - 0.4));
    ctx.lineTo(ax - 8 * Math.cos(angle + 0.4), ay - 8 * Math.sin(angle + 0.4));
    ctx.closePath();
    ctx.fill();

    // State dot
    ctx.beginPath();
    ctx.arc(ax, ay, 5, 0, Math.PI * 2);
    ctx.fillStyle = '#818cf8';
    ctx.fill();

    // Probability display
    ctx.font = '10px system-ui';
    ctx.fillStyle = '#64748b';
    ctx.textAlign = 'center';
    ctx.fillText(`P(|0⟩)=${Math.round(p0 * 100)}%  P(|1⟩)=${Math.round(p1 * 100)}%`, cx, H - 8);
  }, [result, currentStep]);

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
        Bloch Sphere (XZ view)
      </div>
      <canvas ref={canvasRef} width={W} height={H} style={{ borderRadius: 8, display: 'block', maxWidth: '100%' }} />
    </div>
  );
}

// ─── Utility ──────────────────────────────────────────────────────────────────

function RunButton({
  children, onClick, color, outline, small,
}: {
  children: React.ReactNode;
  onClick: () => void;
  color: string;
  outline?: boolean;
  small?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: small ? 1 : undefined,
        width: small ? undefined : '100%',
        padding: small ? '5px 0' : '7px 0',
        fontSize: small ? 11 : 12,
        fontWeight: 700,
        border: `1.5px solid ${color}`,
        borderRadius: 8,
        cursor: 'pointer',
        background: outline ? 'white' : color,
        color: outline ? color : 'white',
        transition: 'all 0.1s',
      }}
    >
      {children}
    </button>
  );
}
